import styled, { css } from 'styled-components';

import {
  Text,
  Container,
  Row,
  Input,
  TextArea,
  Tag,
  Card,
  Box,
  Avatar,
  Button as YogaButton,
} from '@gympass/yoga';
import { Edit } from '@gympass/yoga-icons';
import { wellzTheme } from '@wellz-theme/mui';
import { wellzTheme as wellzTokens } from 'theme/wellzTheme';

import { Button } from 'components/Button';

import { HasVideoSpanProps } from './types';

import * as EndUserStyles from '../EndUser/styles';

export const CareProviderInfoContainer = styled.main`
  display: flex;
  flex-direction: column;
  flex: 1;

  background-color: ${wellzTokens.colors.backgroundLight};
  margin: ${wellzTheme.spacing(0)} auto;
`;

export const HeaderInfoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  padding: ${({ theme }) => theme.yoga.spacing.large}px;
`;

export const ProfileButton = styled(props => <Button {...props} />)`
  padding: ${({ theme }) => theme.yoga.spacing.xxsmall}px
    ${({ theme }) => theme.yoga.spacing.xsmall}px;
  font-size: ${({ theme }) => theme.yoga.fontSizes.small}px;
  line-height: ${({ theme }) => theme.yoga.lineHeights.xsmall}px;
  max-height: ${({ theme }) => theme.yoga.spacing.xlarge}px;
`;

export const HeaderLabel = styled(Text.H4)`
  font-weight: ${({ theme }) => theme.yoga.fontWeights.light};
`;

export const Divisor = styled(EndUserStyles.Divisor)``;

export const ContentContainer = styled.div`
  padding: ${({ theme }) => theme.yoga.spacing.small}px
    ${({ theme }) => theme.yoga.spacing.large}px;
`;

export const CardHeader = styled(Card.Header)`
  margin-bottom: ${({ theme }) => theme.yoga.spacing.large}px;
`;

export const InputsContainer = styled(Container)`
  margin-bottom: ${({ theme }) => theme.yoga.spacing.zero}px;
  display: flex;
  flex-direction: row;
  width: 100%;
`;

export const ContainerRows = styled.div`
  width: 100%;
`;

export const RowsInfo = styled(Row)`
  width: 100%;
  margin: ${({ theme }) => theme.yoga.spacing.zero}px;

  & + & {
    margin-top: ${({ theme }) => theme.yoga.spacing.large}px;
  }
`;

export const InputInfo = styled(Input)`
  color: ${({ theme }) => theme.yoga.colors.stamina} !important;
`;

export const TextAreaInfo = styled(TextArea)`
  border-color: ${({ theme }) => theme.yoga.colors.inputDisabled} !important;
  textarea {
    color: ${({ theme }) => theme.yoga.colors.stamina} !important;
  }

  fieldset {
    min-height: ${({ theme }) => theme.yoga.sizes.textareaMinHeight}px;
  }
`;

export const ExperienceSection = styled.div`
  margin-top: ${({ theme }) => theme.yoga.spacing.large}px;
`;

export const ExperienceSectionTitle = styled(Text.Tiny)`
  color: ${({ theme }) => theme.yoga.colors.deep};
  margin-bottom: ${({ theme }) => theme.yoga.spacing.xsmall}px;
`;

export const ExperienceContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledTag = styled(Tag)`
  color: ${({ theme }) => theme.yoga.colors.stamina};
  border-color: ${({ theme }) => theme.yoga.colors.stamina};

  & + & {
    margin-left: ${({ theme }) => theme.yoga.spacing.xxsmall}px;
  }
`;

export const StyledCard = styled(Card)`
  margin-top: ${({ theme }) => theme.yoga.spacing.large}px;
  padding: ${({ theme }) => theme.yoga.spacing.small}px
    ${({ theme }) => theme.yoga.spacing.large}px
    ${({ theme }) => theme.yoga.spacing.xlarge}px;
`;

export const PersonalInfoContainer = styled(Box).attrs({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
})``;

export const VisualInfoContainer = styled(Box).attrs({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})`
  margin-right: ${({ theme }) => theme.yoga.spacing.xlarge}px;
`;

export const SocialContainer = styled(Box).attrs({
  display: 'flex',
  alignItems: 'center',
})`
  margin-top: ${({ theme }) => theme.yoga.spacing.small}px;
  cursor: pointer;
`;

export const SocialUsername = styled(Text.Small)`
  margin-left: ${({ theme }) => theme.yoga.spacing.xxsmall}px;
`;

export const CareProviderAvatar = styled(Avatar.Circle)`
  width: ${({ theme }) => theme.yoga.sizes.avatarWidth}px;
  height: ${({ theme }) => theme.yoga.sizes.avatarCPInfoHeight}px;
  background-color: transparent;
  cursor: pointer;
  object-fit: cover;

  border: ${({ theme }) => theme.yoga.borders.xlarge}px solid
    ${({ theme }) => theme.yoga.colors.tableBorder};
  box-sizing: border-box !important;
  padding: ${({ theme }) => theme.yoga.spacing.xxsmall}px !important;

  ${({ hasVideo, theme }) =>
    !!hasVideo &&
    css`
      border-color: ${theme.yoga.colors.secondary};
    `}

  img {
    border-radius: ${({ theme }) => theme.yoga.radii.circle}px;
  }
`;

export const CareProviderAvatarText = styled(Text.H5)`
  color: ${({ theme }) => theme.yoga.colors.inputBackground};
  background-color: ${({ theme }) => theme.yoga.colors.primaryLight};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: ${({ theme }) => theme.yoga.radii.circle}px;
`;

export const HasVideoSpan = styled.span<HasVideoSpanProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => theme.yoga.spacing.small}px;
  width: ${({ theme }) => theme.yoga.sizes.hasVideoSpanWidth}px;
  border-radius: ${({ theme }) => theme.yoga.radii.xsmall}px;
  color: ${({ theme }) => theme.yoga.colors.white};
  font-weight: ${({ theme }) => theme.yoga.fontWeights.medium};
  font-size: ${({ theme }) => theme.yoga.fontSizes.xxsmall}px;
  background-color: ${({ theme, hasVideo }) =>
    hasVideo ? theme.yoga.colors.primary : theme.yoga.colors.tableBorder};

  margin-top: ${({ theme }) => theme.yoga.spacing.xsmall}px;
  text-transform: uppercase;
`;

export const PhoneInputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const UpdatePhoneNumberButton = styled(YogaButton.Link)`
  margin-top: ${({ theme }) => theme.yoga.spacing.xxsmall}px;
  margin-left: ${({ theme }) => theme.yoga.spacing.small}px;
  font-size: ${({ theme }) => theme.yoga.fontSizes.small}px;

  svg {
    height: ${({ theme }) => theme.yoga.spacing.small}px;
    width: ${({ theme }) => theme.yoga.spacing.small}px;
  }
`;

export const StyledEdit = styled(Edit)`
  fill: ${({ theme }) => theme.yoga.colors.primary} !important;
`;
